import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Link } from "react-router-dom";
import { LogOut, Upload, Trash2, Pin } from "lucide-react";

const TOKEN_KEY = "fc_admin_token";
const USER_KEY = "fc_admin_user";

const adminClient = (token) =>
  axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` },
  });

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY) || "null"); } catch { return null; }
  });
  const [verifying, setVerifying] = useState(Boolean(localStorage.getItem(TOKEN_KEY)));

  useEffect(() => {
    if (!token) return;
    adminClient(token)
      .get("/admin/verify")
      .then((res) => {
        if (res.data?.user) {
          setUser(res.data.user);
          localStorage.setItem(USER_KEY, JSON.stringify(res.data.user));
        }
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setToken("");
        setUser(null);
        toast.error("Session expired. Please sign in again.");
      })
      .finally(() => setVerifying(false));
  }, [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken("");
    setUser(null);
  };

  if (!token) {
    return (
      <LoginView
        onLogin={(t, u) => {
          localStorage.setItem(TOKEN_KEY, t);
          if (u) localStorage.setItem(USER_KEY, JSON.stringify(u));
          setToken(t);
          setUser(u || null);
          setVerifying(false);
        }}
      />
    );
  }

  if (verifying) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center text-white/60">
        Verifying session…
      </div>
    );
  }

  return <Dashboard token={token} user={user} onLogout={logout} />;
}

function LoginView({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      onLogin(res.data.token, res.data.user);
      toast.success(`Welcome, ${res.data.user?.name || username}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-view"
      className="min-h-screen bg-[#050505] flex items-center justify-center px-6"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-[#0f0f0f] border border-white/10 p-10"
      >
        <Link
          to="/"
          className="text-xs uppercase tracking-[0.2em] text-white/50 hover:text-[#d4af37]"
        >
          ← Back to site
        </Link>
        <div className="mt-6 flex items-center gap-3 mb-6">
          <img
            src="https://customer-assets.emergentagent.com/job_fullcircle-mma/artifacts/hpytiapw_Copilot_20260501_123104.png"
            alt="Full Circle MMA"
            className="h-12 w-auto"
          />
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#d4af37] font-bold">
              Admin Console
            </div>
            <div className="font-heading text-lg uppercase leading-none mt-1 text-white/70">
              Sign in to continue
            </div>
          </div>
        </div>

        <Label
          htmlFor="admin-username"
          className="text-xs uppercase tracking-[0.2em] text-white/60"
        >
          Username
        </Label>
        <Input
          id="admin-username"
          data-testid="admin-username-input"
          required
          autoComplete="username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="mt-2 bg-transparent border-white/15 rounded-none h-12 focus-visible:ring-[#d4af37]"
        />

        <Label
          htmlFor="admin-password"
          className="mt-5 block text-xs uppercase tracking-[0.2em] text-white/60"
        >
          Password
        </Label>
        <Input
          id="admin-password"
          data-testid="admin-password-input"
          type="password"
          required
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-2 bg-transparent border-white/15 rounded-none h-12 focus-visible:ring-[#d4af37]"
        />

        <button
          type="submit"
          data-testid="admin-login-submit"
          disabled={loading}
          className="mt-6 w-full px-6 py-4 bg-[#d4af37] text-black text-xs font-bold uppercase tracking-[0.25em] hover:bg-[#f1c40f] transition-colors disabled:opacity-50"
        >
          {loading ? "Signing in…" : "Sign In"}
        </button>
      </form>
    </div>
  );
}

function Dashboard({ token, user, onLogout }) {
  const [tab, setTab] = useState("notices");
  const [pwOpen, setPwOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_fullcircle-mma/artifacts/hpytiapw_Copilot_20260501_123104.png"
              alt="Full Circle MMA"
              className="h-9 w-auto"
            />
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#d4af37] font-bold">
              Admin
            </div>
            {user && (
              <div className="hidden md:flex items-center gap-2 ml-3 pl-3 border-l border-white/10">
                <span className="text-[10px] uppercase tracking-[0.25em] text-white/40">
                  Signed in
                </span>
                <span data-testid="admin-current-user" className="text-xs text-white/85 font-semibold">
                  {user.name || user.username}
                </span>
                {user.coach_id && (
                  <span className="px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] bg-[#d4af37]/15 text-[#d4af37] border border-[#d4af37]/30">
                    Coach · {user.coach_id}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-change-password"
              onClick={() => setPwOpen(true)}
              className="text-xs uppercase tracking-[0.2em] text-white/60 hover:text-[#d4af37]"
            >
              Change Password
            </button>
            <Link
              to="/"
              className="text-xs uppercase tracking-[0.2em] text-white/60 hover:text-[#d4af37]"
            >
              View site
            </Link>
            <button
              data-testid="admin-logout"
              onClick={onLogout}
              className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.2em] text-white/80 hover:border-[#d4af37] hover:text-[#d4af37]"
            >
              <LogOut size={12} /> Log out
            </button>
          </div>
        </div>

        <nav className="max-w-6xl mx-auto px-6 flex gap-6 text-xs uppercase tracking-[0.2em] border-t border-white/5 overflow-x-auto">
          {["notices", "gallery", "bookings", "coaches", "signups"].map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`py-4 -mb-px border-b-2 transition-colors whitespace-nowrap ${
                tab === t
                  ? "border-[#d4af37] text-[#d4af37]"
                  : "border-transparent text-white/60 hover:text-white"
              }`}
            >
              {t}
            </button>
          ))}
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {tab === "notices" && <NoticesPanel token={token} />}
        {tab === "gallery" && <GalleryPanel token={token} />}
        {tab === "bookings" && <BookingsPanel token={token} />}
        {tab === "coaches" && <CoachesPanel token={token} />}
        {tab === "signups" && <SignupsPanel token={token} />}
      </main>
      {pwOpen && (
        <ChangePasswordModal
          token={token}
          username={user?.username}
          onClose={() => setPwOpen(false)}
        />
      )}
    </div>
  );
}

function ChangePasswordModal({ token, username, onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }
    if (next !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setSaving(true);
    try {
      await adminClient(token).post("/admin/password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated. Use your new password next time you sign in.");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
      data-testid="change-password-modal"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-[#0f0f0f] border border-white/10 p-8"
      >
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#d4af37] font-bold">
          Account
        </div>
        <h3 className="mt-2 font-heading text-2xl uppercase">Change password</h3>
        <p className="mt-2 text-xs text-white/50">
          Signed in as <span className="text-white/80">{username}</span>. Choose a
          password at least 6 characters long. Save it somewhere safe.
        </p>

        <div className="mt-6 space-y-4">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-white/60">
              Current Password
            </Label>
            <Input
              data-testid="pw-current"
              type="password"
              autoComplete="current-password"
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="mt-2 bg-transparent border-white/15 rounded-none h-10 focus-visible:ring-[#d4af37]"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-white/60">
              New Password
            </Label>
            <Input
              data-testid="pw-new"
              type="password"
              autoComplete="new-password"
              required
              minLength={6}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="mt-2 bg-transparent border-white/15 rounded-none h-10 focus-visible:ring-[#d4af37]"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-white/60">
              Confirm New Password
            </Label>
            <Input
              data-testid="pw-confirm"
              type="password"
              autoComplete="new-password"
              required
              minLength={6}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-2 bg-transparent border-white/15 rounded-none h-10 focus-visible:ring-[#d4af37]"
            />
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            data-testid="pw-cancel"
            className="px-5 py-3 border border-white/20 text-white/70 text-[11px] font-bold uppercase tracking-[0.25em] hover:border-white/40 hover:text-white"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="pw-save"
            className="flex-1 px-5 py-3 bg-[#d4af37] text-black text-[11px] font-bold uppercase tracking-[0.25em] hover:bg-[#f1c40f] disabled:opacity-50"
          >
            {saving ? "Saving…" : "Update Password"}
          </button>
        </div>
      </form>
    </div>
  );
}

function NoticesPanel({ token }) {
  const client = adminClient(token);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", pinned: false });
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    try {
      const res = await client.get("/notices");
      setItems(res.data || []);
    } catch {
      // ignore
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await client.post("/admin/notices", form);
      setForm({ title: "", body: "", pinned: false });
      toast.success("Notice published.");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to publish");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this notice?")) return;
    try {
      await client.delete(`/admin/notices/${id}`);
      toast.success("Notice deleted.");
      refresh();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
      <form
        onSubmit={create}
        className="lg:col-span-2 bg-[#0f0f0f] border border-white/10 p-7 h-fit"
        data-testid="admin-notice-form"
      >
        <h3 className="font-heading text-2xl uppercase">New Notice</h3>

        <div className="mt-6">
          <Label className="text-xs uppercase tracking-[0.2em] text-white/60">
            Title
          </Label>
          <Input
            data-testid="admin-notice-title"
            required
            maxLength={140}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="mt-2 bg-transparent border-white/15 rounded-none h-11 focus-visible:ring-[#d4af37]"
          />
        </div>

        <div className="mt-4">
          <Label className="text-xs uppercase tracking-[0.2em] text-white/60">
            Body
          </Label>
          <Textarea
            data-testid="admin-notice-body"
            required
            rows={6}
            maxLength={4000}
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            className="mt-2 bg-transparent border-white/15 rounded-none focus-visible:ring-[#d4af37]"
          />
        </div>

        <div className="mt-4 flex items-center gap-3">
          <Checkbox
            id="admin-notice-pinned"
            data-testid="admin-notice-pinned"
            checked={form.pinned}
            onCheckedChange={(v) => setForm({ ...form, pinned: Boolean(v) })}
            className="border-white/30 data-[state=checked]:bg-[#d4af37] data-[state=checked]:border-[#d4af37] data-[state=checked]:text-black rounded-none"
          />
          <Label
            htmlFor="admin-notice-pinned"
            className="text-sm text-white/80 cursor-pointer inline-flex items-center gap-2"
          >
            <Pin size={12} /> Pin to top
          </Label>
        </div>

        <button
          type="submit"
          data-testid="admin-notice-submit"
          disabled={saving}
          className="mt-6 w-full px-6 py-3 bg-[#d4af37] text-black text-xs font-bold uppercase tracking-[0.25em] hover:bg-[#f1c40f] disabled:opacity-50"
        >
          {saving ? "Publishing…" : "Publish Notice"}
        </button>
      </form>

      <div className="lg:col-span-3">
        <div className="text-xs uppercase tracking-[0.2em] text-white/50 mb-4">
          Published ({items.length})
        </div>
        <div className="space-y-4">
          {items.length === 0 && (
            <div className="border border-dashed border-white/15 p-8 text-center text-sm text-white/50">
              No notices yet.
            </div>
          )}
          {items.map((n) => (
            <div
              key={n.id}
              data-testid={`admin-notice-item-${n.id}`}
              className={`bg-[#0f0f0f] border p-5 ${
                n.pinned ? "border-[#d4af37]/60" : "border-white/10"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">
                    {new Date(n.created_at).toLocaleString("en-AU")}
                    {n.pinned && (
                      <span className="ml-3 text-[#d4af37] inline-flex items-center gap-1">
                        <Pin size={10} /> Pinned
                      </span>
                    )}
                  </div>
                  <div className="font-heading text-xl uppercase mt-1">{n.title}</div>
                  <p className="mt-2 text-sm text-white/70 whitespace-pre-line">
                    {n.body}
                  </p>
                </div>
                <button
                  data-testid={`admin-notice-delete-${n.id}`}
                  onClick={() => remove(n.id)}
                  className="shrink-0 w-9 h-9 border border-white/15 text-white/60 hover:border-red-500 hover:text-red-500 flex items-center justify-center"
                  aria-label="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function GalleryPanel({ token }) {
  const client = adminClient(token);
  const [items, setItems] = useState([]);
  const [files, setFiles] = useState([]);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, failed: 0 });

  const refresh = async () => {
    try {
      const res = await client.get("/gallery");
      setItems(res.data || []);
    } catch {
      // ignore
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (files.length === 0) {
      toast.error("Pick at least one image first");
      return;
    }
    setUploading(true);
    setProgress({ done: 0, total: files.length, failed: 0 });

    let done = 0;
    let failed = 0;

    // Upload in parallel (max 3 at a time) so a batch of 20 doesn't take forever.
    const CONCURRENCY = 3;
    const queue = [...files];
    const workers = Array.from({ length: Math.min(CONCURRENCY, queue.length) }).map(async () => {
      while (queue.length) {
        const f = queue.shift();
        try {
          const fd = new FormData();
          fd.append("file", f);
          if (caption) fd.append("caption", caption);
          await client.post("/admin/gallery", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          done += 1;
        } catch {
          failed += 1;
        }
        setProgress({ done, total: files.length, failed });
      }
    });
    await Promise.all(workers);

    setUploading(false);
    setFiles([]);
    setCaption("");
    const fi = document.getElementById("admin-gallery-file");
    if (fi) fi.value = "";

    if (failed === 0) {
      toast.success(`Uploaded ${done} photo${done === 1 ? "" : "s"}.`);
    } else if (done > 0) {
      toast.error(`${done} uploaded, ${failed} failed.`);
    } else {
      toast.error(`All ${failed} uploads failed.`);
    }
    refresh();
    setTimeout(() => setProgress({ done: 0, total: 0, failed: 0 }), 3500);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      await client.delete(`/admin/gallery/${id}`);
      toast.success("Image removed.");
      refresh();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
      <form
        onSubmit={upload}
        className="lg:col-span-2 bg-[#0f0f0f] border border-white/10 p-7 h-fit"
        data-testid="admin-gallery-form"
      >
        <h3 className="font-heading text-2xl uppercase">Upload Photos</h3>
        <p className="mt-2 text-[11px] text-white/50 leading-relaxed">
          Select one or many at once — hold <span className="text-white/80">Ctrl</span>/<span className="text-white/80">⌘</span> or drag-select in the picker.
        </p>

        <div className="mt-6">
          <Label className="text-xs uppercase tracking-[0.2em] text-white/60">
            Images (JPG · PNG · WEBP · max 10 MB each)
          </Label>
          <input
            id="admin-gallery-file"
            data-testid="admin-gallery-file"
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={(e) => setFiles(Array.from(e.target.files || []))}
            className="mt-2 block w-full text-sm text-white/70 file:mr-4 file:py-2.5 file:px-4 file:border-0 file:bg-[#d4af37] file:text-black file:uppercase file:tracking-[0.2em] file:text-[10px] file:font-bold hover:file:bg-[#f1c40f] cursor-pointer"
          />
          {files.length > 0 && (
            <div
              data-testid="admin-gallery-selected-count"
              className="mt-3 text-[11px] uppercase tracking-[0.2em] text-[#d4af37] font-bold"
            >
              {files.length} file{files.length === 1 ? "" : "s"} selected
              <button
                type="button"
                onClick={() => {
                  setFiles([]);
                  const fi = document.getElementById("admin-gallery-file");
                  if (fi) fi.value = "";
                }}
                className="ml-3 text-white/40 hover:text-red-400 normal-case tracking-normal font-normal underline"
              >
                clear
              </button>
            </div>
          )}
        </div>

        <div className="mt-4">
          <Label className="text-xs uppercase tracking-[0.2em] text-white/60">
            Caption (optional · applied to all)
          </Label>
          <Input
            data-testid="admin-gallery-caption"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="e.g. Sparring · Sat 12 May"
            className="mt-2 bg-transparent border-white/15 rounded-none h-11 focus-visible:ring-[#d4af37]"
          />
        </div>

        {uploading && progress.total > 0 && (
          <div className="mt-5" data-testid="admin-gallery-progress">
            <div className="flex justify-between text-[10px] uppercase tracking-[0.2em] text-white/60 mb-1.5">
              <span>Uploading</span>
              <span>{progress.done} / {progress.total}{progress.failed ? ` · ${progress.failed} failed` : ""}</span>
            </div>
            <div className="h-1 bg-black/50 border border-white/10 overflow-hidden">
              <div
                className="h-full bg-[#d4af37] transition-all duration-300"
                style={{ width: `${(progress.done / progress.total) * 100}%` }}
              />
            </div>
          </div>
        )}

        <button
          type="submit"
          data-testid="admin-gallery-submit"
          disabled={uploading}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#d4af37] text-black text-xs font-bold uppercase tracking-[0.25em] hover:bg-[#f1c40f] disabled:opacity-50 btn-lift"
        >
          <Upload size={14} />
          {uploading ? `Uploading ${progress.done}/${progress.total}…` : files.length > 1 ? `Upload ${files.length} photos` : "Upload"}
        </button>
      </form>

      <div className="lg:col-span-3">
        <div className="text-xs uppercase tracking-[0.2em] text-white/50 mb-4">
          Photos ({items.length})
        </div>
        {items.length === 0 ? (
          <div className="border border-dashed border-white/15 p-8 text-center text-sm text-white/50">
            No photos uploaded yet.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {items.map((img) => (
              <div
                key={img.id}
                data-testid={`admin-gallery-item-${img.id}`}
                className="relative group aspect-square bg-[#111] border border-white/10 overflow-hidden"
              >
                <img
                  src={`${API}/gallery/image/${img.id}`}
                  alt={img.caption || ""}
                  className="w-full h-full object-cover"
                />
                <button
                  data-testid={`admin-gallery-delete-${img.id}`}
                  onClick={() => remove(img.id)}
                  className="absolute top-2 right-2 w-9 h-9 bg-black/80 border border-white/20 text-white hover:border-red-500 hover:text-red-500 focus:opacity-100 focus:border-red-500 focus:text-red-500 flex items-center justify-center md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                  aria-label="Delete"
                >
                  <Trash2 size={14} />
                </button>
                {img.caption && (
                  <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/90 to-transparent text-[11px] text-white/80 line-clamp-2">
                    {img.caption}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SignupsPanel({ token }) {
  const client = adminClient(token);
  const [bookings, setBookings] = useState([]);
  const [memberships, setMemberships] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [b, m] = await Promise.all([
          client.get("/admin/bookings"),
          client.get("/admin/memberships"),
        ]);
        setBookings(b.data || []);
        setMemberships(m.data || []);
      } catch {
        // ignore
      }
    })();
    /* eslint-disable-next-line */
  }, []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <SignupTable
        title="Private Bookings"
        rows={bookings}
        columns={["full_name", "email", "phone", "coach_name", "package_label", "reference", "preferred_date", "preferred_time", "payment_status", "confirmation_status"]}
      />
      <SignupTable
        title="Memberships / Enquiries"
        rows={memberships}
        columns={["full_name", "email", "phone", "preferred_start_date", "interest", "hear_about_us", "status"]}
      />
    </div>
  );
}

function BookingsPanel({ token }) {
  const client = adminClient(token);
  const [bookings, setBookings] = useState([]);
  const [busy, setBusy] = useState({});

  const refresh = async () => {
    try {
      const res = await client.get("/admin/bookings");
      setBookings(res.data || []);
    } catch {
      // ignore
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const decide = async (id, decision) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await client.post(`/admin/bookings/${id}/confirm`, { decision });
      toast.success(decision === "confirmed" ? "Booking confirmed." : "Booking rejected.");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const markPaid = async (id, paid) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await client.post(`/admin/bookings/${id}/mark-paid`, { paid });
      toast.success(paid ? "Marked as paid." : "Marked as unpaid.");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const pending = bookings.filter((b) => b.confirmation_status === "pending_coach_confirmation");
  const decided = bookings.filter((b) => b.confirmation_status !== "pending_coach_confirmation");

  return (
    <div className="space-y-10">
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="font-heading text-xl uppercase">Pending Coach Confirmation</div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#d4af37]">
            {pending.length} awaiting
          </div>
        </div>
        {pending.length === 0 ? (
          <div className="border border-dashed border-white/15 p-8 text-center text-sm text-white/50">
            No bookings waiting for coach confirmation.
          </div>
        ) : (
          <div className="space-y-3">
            {pending.map((b) => (
              <div
                key={b.id}
                data-testid={`admin-booking-${b.id}`}
                className="bg-[#0f0f0f] border border-[#d4af37]/40 p-5 flex flex-col md:flex-row md:items-center justify-between gap-4"
              >
                <div className="space-y-1">
                  <div className="font-heading text-lg uppercase">{b.full_name}</div>
                  <div className="text-sm text-white/70">
                    {b.package_label} · with{" "}
                    <span className="text-[#d4af37]">{b.coach_name || "—"}</span>
                  </div>
                  <div className="text-xs text-white/50">
                    {b.preferred_date} · {b.preferred_time} · {b.email} · {b.phone}
                  </div>
                  {b.notes && (
                    <div className="text-xs text-white/60 italic mt-1">"{b.notes}"</div>
                  )}
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mt-2">
                    {b.reference && (
                      <span className="text-[#d4af37] mr-3">Ref: {b.reference}</span>
                    )}
                    Payment: <span className={b.payment_status === "paid" ? "text-[#d4af37]" : "text-white/60"}>{b.payment_status}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  {b.payment_status !== "paid" && (
                    <button
                      data-testid={`admin-booking-mark-paid-${b.id}`}
                      onClick={() => markPaid(b.id, true)}
                      disabled={busy[b.id]}
                      className="px-4 py-2 border border-[#d4af37] text-[#d4af37] text-[11px] font-bold uppercase tracking-[0.2em] hover:bg-[#d4af37] hover:text-black disabled:opacity-50"
                    >
                      Mark Paid
                    </button>
                  )}
                  <button
                    data-testid={`admin-booking-confirm-${b.id}`}
                    onClick={() => decide(b.id, "confirmed")}
                    disabled={busy[b.id]}
                    className="px-4 py-2 bg-[#d4af37] text-black text-[11px] font-bold uppercase tracking-[0.2em] hover:bg-[#f1c40f] disabled:opacity-50"
                  >
                    Confirm
                  </button>
                  <button
                    data-testid={`admin-booking-reject-${b.id}`}
                    onClick={() => decide(b.id, "rejected")}
                    disabled={busy[b.id]}
                    className="px-4 py-2 border border-white/20 text-white/80 text-[11px] font-bold uppercase tracking-[0.2em] hover:border-red-500 hover:text-red-500 disabled:opacity-50"
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="font-heading text-xl uppercase">Decision History</div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-white/50">
              {decided.length}
            </div>
          </div>
          {decided.length > 0 && (
            <button
              data-testid="admin-clear-history"
              onClick={async () => {
                if (!window.confirm(`Clear ${decided.length} past bookings from history? This can't be undone.`)) return;
                try {
                  const res = await client.delete("/admin/bookings/history");
                  toast.success(`Cleared ${res.data.deleted} bookings.`);
                  refresh();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Clear failed");
                }
              }}
              className="text-[11px] uppercase tracking-[0.2em] text-white/50 hover:text-red-400 border border-white/15 hover:border-red-400/50 px-3 py-1.5"
            >
              Clear History
            </button>
          )}
        </div>
        <SignupTable
          title=""
          rows={decided}
          columns={["full_name", "coach_name", "package_label", "reference", "preferred_date", "preferred_time", "confirmation_status", "payment_status"]}
        />
      </div>
    </div>
  );
}

function CoachesPanel({ token }) {
  const client = adminClient(token);
  const [coaches, setCoaches] = useState([]);

  const refresh = async () => {
    try {
      const res = await client.get("/admin/coaches");
      setCoaches(res.data || []);
    } catch {
      // ignore
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  return (
    <div>
      <div className="bg-[#0f0f0f] border border-[#d4af37]/30 p-5 mb-8">
        <div className="text-[11px] uppercase tracking-[0.25em] text-[#d4af37] font-bold">
          Per-Coach Payments — Bank Transfer / PayID
        </div>
        <p className="mt-2 text-sm text-white/70 leading-relaxed">
          Set each coach's PayID and/or bank details below. Customers see these
          on the booking confirmation screen and pay direct to the coach,
          referencing their booking ID. Use the "Mark Paid" button on the
          Bookings tab once funds arrive.
        </p>
      </div>
      <div className="space-y-6">
        {coaches.map((c) => (
          <CoachCard key={c.id} coach={c} client={client} />
        ))}
      </div>
    </div>
  );
}

function CoachCard({ coach, client }) {
  const [schedule, setSchedule] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newBlock, setNewBlock] = useState({ start_at: "", end_at: "", note: "" });
  const [busy, setBusy] = useState({});
  const [payment, setPayment] = useState(null);
  const [savingPayment, setSavingPayment] = useState(false);

  const refreshAll = async () => {
    try {
      const [s, b, p] = await Promise.all([
        client.get(`/admin/coaches/${coach.id}/schedule`),
        client.get(`/admin/coaches/${coach.id}/blocks`),
        client.get(`/admin/coaches/${coach.id}/payment-details`),
      ]);
      setSchedule(s.data);
      setBlocks(b.data || []);
      setPayment(p.data);
    } catch {
      // ignore
    }
  };
  useEffect(() => { refreshAll(); /* eslint-disable-next-line */ }, []);

  if (!schedule || !payment) {
    return (
      <div className="bg-[#0f0f0f] border border-white/10 p-5 text-sm text-white/50">
        Loading {coach.name}…
      </div>
    );
  }

  const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const updateDay = (key, field, value) => {
    const wh = { ...(schedule.weekly_hours || {}) };
    if (!wh[key]) wh[key] = { start: "06:00", end: "18:30" };
    wh[key] = { ...wh[key], [field]: value };
    setSchedule({ ...schedule, weekly_hours: wh });
  };

  const toggleDay = (key) => {
    const wh = { ...(schedule.weekly_hours || {}) };
    wh[key] = wh[key] ? null : { start: "06:00", end: "18:30" };
    setSchedule({ ...schedule, weekly_hours: wh });
  };

  const saveSchedule = async () => {
    setSaving(true);
    try {
      await client.put(`/admin/coaches/${coach.id}/schedule`, {
        weekly_hours: schedule.weekly_hours,
        class_block: schedule.class_block,
      });
      toast.success("Schedule updated.");
      refreshAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const addBlock = async (e) => {
    e.preventDefault();
    if (!newBlock.start_at || !newBlock.end_at) {
      toast.error("Pick a start and end time.");
      return;
    }
    try {
      await client.post(`/admin/coaches/${coach.id}/blocks`, newBlock);
      setNewBlock({ start_at: "", end_at: "", note: "" });
      toast.success("Block added.");
      refreshAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add block");
    }
  };

  const removeBlock = async (id) => {
    if (!window.confirm("Remove this block?")) return;
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await client.delete(`/admin/coaches/${coach.id}/blocks/${id}`);
      toast.success("Block removed.");
      refreshAll();
    } catch {
      toast.error("Delete failed");
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const savePayment = async () => {
    setSavingPayment(true);
    try {
      await client.put(`/admin/coaches/${coach.id}/payment-details`, {
        account_name: payment.account_name || "",
        bsb: payment.bsb || "",
        account_number: payment.account_number || "",
        payid: payment.payid || "",
        payid_type: payment.payid_type || "",
        instructions: payment.instructions || "",
        notify_email: payment.notify_email || "",
        notify_phone: payment.notify_phone || "",
      });
      toast.success("Payment details saved.");
      refreshAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSavingPayment(false);
    }
  };

  return (
    <div
      data-testid={`admin-coach-${coach.id}`}
      className="bg-[#0f0f0f] border border-white/10"
    >
      {/* Header */}
      <div className="p-5 border-b border-white/10 flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#d4af37]">
            {coach.role}
          </div>
          <div className="font-heading text-2xl uppercase mt-1">{coach.name}</div>
        </div>
        <div className="text-xs">
          <span
            className={`inline-flex items-center gap-2 uppercase tracking-[0.2em] font-bold ${
              payment.configured ? "text-[#d4af37]" : "text-white/50"
            }`}
          >
            <span
              className={`w-2 h-2 ${
                payment.configured ? "bg-[#d4af37]" : "bg-white/30"
              }`}
            />
            {payment.configured ? "Payment details set" : "Payment details — not set"}
          </span>
        </div>
      </div>

      {/* Payment Details editor */}
      <div className="p-5 border-b border-white/10" data-testid={`admin-coach-payment-${coach.id}`}>
        <div className="text-[11px] uppercase tracking-[0.25em] text-white/50 mb-4">
          Payment Details — Bank Transfer / PayID
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-white/60">
              PayID
            </Label>
            <Input
              data-testid={`admin-coach-payid-${coach.id}`}
              value={payment.payid || ""}
              onChange={(e) => setPayment({ ...payment, payid: e.target.value })}
              placeholder="e.g. 0412 345 678 or you@email.com"
              className="mt-2 bg-transparent border-white/15 rounded-none h-10 focus-visible:ring-[#d4af37]"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-white/60">
              PayID Type
            </Label>
            <select
              data-testid={`admin-coach-payid-type-${coach.id}`}
              value={payment.payid_type || ""}
              onChange={(e) => setPayment({ ...payment, payid_type: e.target.value })}
              className="mt-2 w-full bg-transparent border border-white/15 rounded-none h-10 px-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#d4af37]"
            >
              <option value="" className="bg-black">— Select —</option>
              <option value="phone" className="bg-black">Phone</option>
              <option value="email" className="bg-black">Email</option>
              <option value="abn" className="bg-black">ABN</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-white/60">
              Account Name
            </Label>
            <Input
              data-testid={`admin-coach-acct-name-${coach.id}`}
              value={payment.account_name || ""}
              onChange={(e) => setPayment({ ...payment, account_name: e.target.value })}
              placeholder="Full account holder name"
              className="mt-2 bg-transparent border-white/15 rounded-none h-10 focus-visible:ring-[#d4af37]"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-white/60">
              BSB
            </Label>
            <Input
              data-testid={`admin-coach-bsb-${coach.id}`}
              value={payment.bsb || ""}
              onChange={(e) => setPayment({ ...payment, bsb: e.target.value })}
              placeholder="012-345"
              className="mt-2 bg-transparent border-white/15 rounded-none h-10 focus-visible:ring-[#d4af37]"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-white/60">
              Account Number
            </Label>
            <Input
              data-testid={`admin-coach-acct-${coach.id}`}
              value={payment.account_number || ""}
              onChange={(e) => setPayment({ ...payment, account_number: e.target.value })}
              placeholder="1234 5678"
              className="mt-2 bg-transparent border-white/15 rounded-none h-10 focus-visible:ring-[#d4af37]"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-white/60">
              Booking Notification Email
            </Label>
            <Input
              data-testid={`admin-coach-notify-${coach.id}`}
              type="email"
              value={payment.notify_email || ""}
              onChange={(e) => setPayment({ ...payment, notify_email: e.target.value })}
              placeholder="coach@email.com — where booking alerts go for this coach"
              className="mt-2 bg-transparent border-white/15 rounded-none h-10 focus-visible:ring-[#d4af37]"
            />
            <div className="mt-1.5 text-[10px] text-white/40 leading-relaxed">
              When someone books this coach, the alert goes here. Leave blank to use the gym default.
            </div>
          </div>
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-white/60">
              Booking Notification Phone (SMS · optional)
            </Label>
            <Input
              data-testid={`admin-coach-notify-phone-${coach.id}`}
              type="tel"
              value={payment.notify_phone || ""}
              onChange={(e) => setPayment({ ...payment, notify_phone: e.target.value })}
              placeholder="e.g. 0494342796 — Australian mobile"
              className="mt-2 bg-transparent border-white/15 rounded-none h-10 focus-visible:ring-[#d4af37]"
            />
            <div className="mt-1.5 text-[10px] text-white/40 leading-relaxed">
              Optional. When someone books this coach, a text is sent here (once Twilio env vars are configured).
            </div>
          </div>
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-white/60">
              Extra Instructions (optional)
            </Label>
            <Textarea
              data-testid={`admin-coach-payment-instructions-${coach.id}`}
              rows={2}
              value={payment.instructions || ""}
              onChange={(e) => setPayment({ ...payment, instructions: e.target.value })}
              placeholder="Anything else customers should know about paying you."
              className="mt-2 bg-transparent border-white/15 rounded-none focus-visible:ring-[#d4af37]"
            />
          </div>
        </div>
        <button
          onClick={savePayment}
          disabled={savingPayment}
          data-testid={`admin-coach-payment-save-${coach.id}`}
          className="mt-5 px-5 py-2.5 bg-[#d4af37] text-black text-[11px] font-bold uppercase tracking-[0.25em] hover:bg-[#f1c40f] disabled:opacity-50"
        >
          {savingPayment ? "Saving…" : "Save Payment Details"}
        </button>
      </div>

      {/* Weekly hours editor */}
      <div className="p-5 border-b border-white/10">
        <div className="text-[11px] uppercase tracking-[0.25em] text-white/50 mb-4">
          Weekly Booking Hours
        </div>
        <div className="space-y-2">
          {dayNames.map((name, idx) => {
            const key = String(idx);
            const dh = (schedule.weekly_hours || {})[key];
            return (
              <div
                key={key}
                className="flex items-center gap-3 text-sm"
                data-testid={`admin-coach-day-${coach.id}-${name.toLowerCase()}`}
              >
                <div className="w-14 font-heading uppercase">{name}</div>
                <button
                  type="button"
                  onClick={() => toggleDay(key)}
                  className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] border ${
                    dh
                      ? "bg-[#d4af37] text-black border-[#d4af37]"
                      : "text-white/50 border-white/20"
                  }`}
                >
                  {dh ? "On" : "Off"}
                </button>
                {dh && (
                  <>
                    <input
                      type="time"
                      value={dh.start}
                      onChange={(e) => updateDay(key, "start", e.target.value)}
                      className="bg-transparent border border-white/15 rounded-none h-9 px-2 text-sm"
                    />
                    <span className="text-white/40">→</span>
                    <input
                      type="time"
                      value={dh.end}
                      onChange={(e) => updateDay(key, "end", e.target.value)}
                      className="bg-transparent border border-white/15 rounded-none h-9 px-2 text-sm"
                    />
                  </>
                )}
              </div>
            );
          })}
        </div>
        <div className="mt-5 flex items-center gap-3 text-xs text-white/60">
          <span className="uppercase tracking-[0.2em]">Class block:</span>
          <input
            type="time"
            value={schedule.class_block?.start || "17:30"}
            onChange={(e) =>
              setSchedule({
                ...schedule,
                class_block: { ...(schedule.class_block || {}), start: e.target.value },
              })
            }
            className="bg-transparent border border-white/15 rounded-none h-9 px-2"
          />
          <span className="text-white/40">→</span>
          <input
            type="time"
            value={schedule.class_block?.end || "18:30"}
            onChange={(e) =>
              setSchedule({
                ...schedule,
                class_block: { ...(schedule.class_block || {}), end: e.target.value },
              })
            }
            className="bg-transparent border border-white/15 rounded-none h-9 px-2"
          />
        </div>
        <button
          onClick={saveSchedule}
          disabled={saving}
          data-testid={`admin-coach-save-${coach.id}`}
          className="mt-5 px-5 py-2.5 bg-[#d4af37] text-black text-[11px] font-bold uppercase tracking-[0.25em] hover:bg-[#f1c40f] disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save Hours"}
        </button>
      </div>

      {/* Blocks */}
      <div className="p-5">
        <div className="text-[11px] uppercase tracking-[0.25em] text-white/50 mb-4">
          One-Off Blocks
        </div>

        <form
          onSubmit={addBlock}
          className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1.2fr_auto] gap-3 mb-4"
        >
          <input
            type="datetime-local"
            value={newBlock.start_at}
            data-testid={`admin-coach-block-start-${coach.id}`}
            onChange={(e) => setNewBlock({ ...newBlock, start_at: e.target.value })}
            className="bg-transparent border border-white/15 rounded-none h-10 px-3 text-sm"
            required
          />
          <input
            type="datetime-local"
            value={newBlock.end_at}
            data-testid={`admin-coach-block-end-${coach.id}`}
            onChange={(e) => setNewBlock({ ...newBlock, end_at: e.target.value })}
            className="bg-transparent border border-white/15 rounded-none h-10 px-3 text-sm"
            required
          />
          <input
            type="text"
            placeholder="Note (optional)"
            value={newBlock.note}
            data-testid={`admin-coach-block-note-${coach.id}`}
            onChange={(e) => setNewBlock({ ...newBlock, note: e.target.value })}
            className="bg-transparent border border-white/15 rounded-none h-10 px-3 text-sm"
          />
          <button
            type="submit"
            data-testid={`admin-coach-block-add-${coach.id}`}
            className="px-4 py-2.5 bg-[#d4af37] text-black text-[11px] font-bold uppercase tracking-[0.25em] hover:bg-[#f1c40f]"
          >
            Block Off
          </button>
        </form>

        {blocks.length === 0 ? (
          <div className="border border-dashed border-white/15 p-5 text-center text-xs text-white/50">
            No upcoming blocks.
          </div>
        ) : (
          <div className="space-y-2">
            {blocks.map((b) => (
              <div
                key={b.id}
                data-testid={`admin-coach-block-${b.id}`}
                className="flex items-center justify-between gap-3 border border-white/10 px-4 py-3 text-sm"
              >
                <div>
                  <div className="text-white/85">
                    {new Date(b.start_at).toLocaleString("en-AU")} →{" "}
                    {new Date(b.end_at).toLocaleString("en-AU")}
                  </div>
                  {b.note && (
                    <div className="text-xs text-white/50 italic mt-0.5">{b.note}</div>
                  )}
                </div>
                <button
                  onClick={() => removeBlock(b.id)}
                  disabled={busy[b.id]}
                  data-testid={`admin-coach-block-delete-${b.id}`}
                  className="text-[10px] uppercase tracking-[0.2em] border border-white/20 px-3 py-2 text-white/70 hover:border-red-500 hover:text-red-500 disabled:opacity-50"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SignupTable({ title, rows, columns }) {
  return (
    <div className="bg-[#0f0f0f] border border-white/10">
      {title && (
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div className="font-heading text-xl uppercase">{title}</div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-white/50">
            {rows.length}
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        {rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-white/50">
            No entries yet.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.25em] text-white/40 border-b border-white/10">
                {columns.map((c) => (
                  <th key={c} className="text-left font-medium px-4 py-3">
                    {c.replace(/_/g, " ")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr
                  key={r.id || i}
                  className="border-b border-white/5 hover:bg-white/[0.02]"
                >
                  {columns.map((c) => (
                    <td key={c} className="px-4 py-3 text-white/80">
                      {String(r[c] ?? "—")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
